import {createNewRegion, deleteRegion, getAvailableRegions, updateRegion} from '../../../../api/settings';
import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogTitle, Snackbar, TextField, Dialog, DialogContent, DialogContentText } from '@mui/material';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import styles from './RegionSettingsComponent.module.scss';

type RegionType = {
  id: string;
  name: string;
}

export const RegionsSettingsComponent = () => {
  const [regionList, setRegionList] = useState <RegionType[] | []>([]);
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
  const [alert, setAlert] = useState({
    isSuccess: false,
    message: ''
  });
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  
  useEffect(() => {
    (async () => {
      setRegionList(await getAvailableRegions());
    })();
  }, []);
  
  const handleChangeRegion = (id: string, newRegionName: string) => {
    const updatedRegionList = regionList.map((item) => {
      if (item.id === id) {
        return {...item, name: newRegionName.toUpperCase()}
      }
      return item
    });
    setRegionList(updatedRegionList);
  };
  
  const handleUpdateRegion = async (id: string, newRegionName: string) => {
    try {
      await updateRegion(id, newRegionName);
      setAlert({ isSuccess: true, message: 'Region updated successfully'});
    } catch (e) {
      setAlert({ isSuccess: false, message: 'Failed to save region name, please try again later or use a different name'});
    }
    setIsOpenSnackbar(true);
  }
  
  const handleDeleteRegion = async (id: string) => {
    try {
      await deleteRegion(id);
      const updatedRegionList = regionList.filter((item) => item.id !== id);
      setRegionList(updatedRegionList);
      setAlert({ isSuccess: true, message: 'Region deleted successfully'});
    } catch (e) {
      setAlert({ isSuccess: false, message: 'An error occurred while deleting the region'});
    }
    setIsOpenSnackbar(true);
  };
  
  const handleCreateNewRegion = async () => {
    if (newRegionName.length > 1) {
      try {
        const newRegion = await createNewRegion(newRegionName);
        setRegionList([...regionList, newRegion]);
        setAlert({ isSuccess: true, message: 'Region has been successfully created'});
        setIsOpenDialog(false);
        setNewRegionName('');
      } catch (e) {
        setAlert({ isSuccess: false, message: 'Region not created, please try again later or use a different name'});
      }
    } else {
      setAlert({ isSuccess: false, message: 'Region name must have more than one character'});
    }
    
    setIsOpenSnackbar(true);
  }
  
  return (
    <div className={styles.mainContainer}>
      <div className={styles.regionList}>
        {regionList.map((item: RegionType, index: number) => (
          <div className={styles.regionItem}>
            <span>{index + 1}.</span>
            <TextField
              label="Region name"
              margin="normal"
              id={item.id}
              value={item.name}
              onChange={(event) => handleChangeRegion(item.id, event.target.value)}
              onBlur={(event) => handleUpdateRegion(item.id, event.target.value)}
            />
            <DeleteTwoToneIcon color="error" className="cursor-pointer ml-3" onClick={() => handleDeleteRegion(item.id)} />
          </div>
        ))}
      </div>
      <Button variant="contained" className="!mt-10" onClick={() => setIsOpenDialog(true)}>
        + Add new region
      </Button>
      <Dialog open={isOpenDialog} onClose={() => {
        setNewRegionName('');
        setIsOpenDialog(false);
      }}>
        <DialogTitle>Create new region</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To create a new region: give it a name and click the create button, after successfully creating a new region you will receive a native notification.
          </DialogContentText>
          <TextField
            label="New region name"
            margin="normal"
            id="new-region"
            value={newRegionName}
            onChange={(event) => setNewRegionName(event.target.value.toUpperCase())}
          />
          <div className="flex justify-between content-center mt-5">
            <Button variant="outlined" onClick={() => {
              setIsOpenDialog(false);
              setNewRegionName('');
            }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => handleCreateNewRegion()}>
              Create new region
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Snackbar
        sx={{ height: "5%" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={isOpenSnackbar}
        autoHideDuration={5000}
        onClose={() => setIsOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setIsOpenSnackbar(false)}
          severity={alert.isSuccess ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </div>
  )
}